import { FC, useMemo } from "react";

import { UserAvatar } from "@/domain/user/components/user-avatar";
import { useSmartUserList } from "@/domain/user/hooks/api/use-smart-user-list";
import { cn } from "@/lib/cn";
import type { NodeViewProps } from "@tiptap/react";
import { NodeViewWrapper } from "@tiptap/react";

/**
 * Mention node component for Tiptap editor.
 *
 * **Security**: Uses `useSmartUserList` to fetch user data safely in both
 * public and private contexts:
 * - Public dashboards: Shows sanitized user data (no email/username), no avatar
 * - Private dashboards: Shows full user data with avatar
 *
 * **Fallback**: If user not found, displays the stored label or userId.
 */
export const MentionNode: FC<NodeViewProps> = (props) => {
  const { node } = props;
  const userId = node.attrs.id;
  const label = node.attrs.label;

  // SECURITY: Use smart hook that switches between public/private data sources
  const { userList, isPublic } = useSmartUserList();

  const user = useMemo(() => {
    if (!userId || !userList) {
      return null;
    }
    return userList.find((u) => u.id === userId) || null;
  }, [userId, userList]);

  const displayName = useMemo(() => {
    if (user) {
      return user.fullname || user.username || user.email || "";
    }
    return label || userId || "";
  }, [user, label, userId]);

  return (
    <NodeViewWrapper
      className={cn(
        "mention-node inline-flex items-baseline gap-1 rounded-sm px-1.5 py-0.5 align-baseline",
        "bg-primary/10 text-primary",
        "cursor-default",
      )}
      data-type="mention"
      data-id={userId}
      data-label={displayName}
    >
      {/* SECURITY: Only show avatar in private mode to avoid loading user data in public dashboards */}
      {user && !isPublic && (
        <span className="inline-flex items-center shrink-0 align-middle">
          <UserAvatar user={user} size="xs" />
        </span>
      )}
      <span className="font-medium">{displayName}</span>
    </NodeViewWrapper>
  );
};

